/**
 * Sound Notifications settings panel — master mute/volume/voice, severity,
 * per-event Off|Beep|Voice, preview/test, reset defaults.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { composeWorkerPhraseTokens } from '../lib/notification-events';
import {
  DEFAULT_NOTIFICATION_SOUND_SETTINGS,
  NOTIFICATION_EVENT_LABELS,
  NOTIFICATION_SEVERITY_LABELS,
  recallNotificationSoundSettings,
  rememberNotificationSoundSettings,
  resetNotificationSoundSettings,
  setEventSoundMode,
  setMasterEnabled,
  setNotificationVolume,
  setSeverityBehavior,
  setVoiceEnabled,
  type EventSoundMode,
  type NotificationEventKey,
  type NotificationSeverityKey,
  type NotificationSoundSettings,
  type SeverityBehavior,
} from '../lib/notification-sound-settings';
import { getSharedNotificationPlayer } from '../lib/notification-playback';
import { OWNER_NEEDED_DELIVERY_NOTE } from '../lib/owner-needed';
import '../notification-sound-settings.css';

const EVENT_KEYS = Object.keys(NOTIFICATION_EVENT_LABELS) as NotificationEventKey[];
const SEVERITY_KEYS = Object.keys(NOTIFICATION_SEVERITY_LABELS) as NotificationSeverityKey[];

function ToggleRow(props: {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly checked: boolean;
  readonly onToggle: (next: boolean) => void;
}): ReactElement {
  const { id, label, description, checked, onToggle } = props;
  return (
    <label className="notification-sound-settings-row" htmlFor={id}>
      <span className="notification-sound-settings-row-text">
        <span className="notification-sound-settings-row-label">{label}</span>
        <span className="notification-sound-settings-row-desc">{description}</span>
      </span>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => onToggle(event.target.checked)}
      />
    </label>
  );
}

export function NotificationSoundSettings(): ReactElement {
  const player = useMemo(() => getSharedNotificationPlayer(), []);
  const [settings, setSettings] = useState<NotificationSoundSettings>(() => recallNotificationSoundSettings());
  const [statusLine, setStatusLine] = useState('Ready to preview.');
  const baseId = 'notification-sound-settings';

  useEffect(() => {
    player.setSettings(settings);
    rememberNotificationSoundSettings(settings);
  }, [player, settings]);

  useEffect(() => {
    const unlock = () => player.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    void player.ensureLoaded().then((ok) => {
      if (!ok) setStatusLine('Audio pack not loaded yet — previews may be silent.');
    });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [player]);

  const persist = useCallback((next: NotificationSoundSettings) => {
    setSettings(next);
  }, []);

  const runPreview = useCallback(async (tokenIds: readonly string[], label: string) => {
    player.unlock();
    player.setSettings(settings);
    const ok = await player.playTokenIds(tokenIds, { label, debounceKey: null });
    setStatusLine(ok ? `Playing: ${label}` : settings.masterEnabled ? 'Preview skipped (missing audio or empty plan).' : 'Muted — visual notifications still show.');
  }, [player, settings]);

  const muted = !settings.masterEnabled;

  return (
    <section
      className="notification-sound-settings"
      aria-labelledby={`${baseId}-heading`}
      data-enabled={settings.masterEnabled ? 'true' : 'false'}
      data-testid="notification-sound-settings"
    >
      <header className="notification-sound-settings-header">
        <h2 id={`${baseId}-heading`}>Sound Notifications</h2>
        <p className="notification-sound-settings-lede">
          Composable beeps and voice tokens for Worker events. Sound is never the only
          signal — the desk still shows visual notices when muted.
        </p>
      </header>

      <ToggleRow
        id={`${baseId}-master`}
        label="Sound notifications"
        description="Master On/Off. When off, every beep and voice clip stays silent."
        checked={settings.masterEnabled}
        onToggle={(next) => persist(setMasterEnabled(settings, next))}
      />

      <label className="notification-sound-settings-row" htmlFor={`${baseId}-volume`}>
        <span className="notification-sound-settings-row-text">
          <span className="notification-sound-settings-row-label">Volume</span>
          <span className="notification-sound-settings-row-desc">{Math.round(settings.volume * 100)}%</span>
        </span>
        <input
          id={`${baseId}-volume`}
          className="notification-sound-settings-volume"
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={settings.volume}
          disabled={!settings.masterEnabled}
          onChange={(event) => persist(setNotificationVolume(settings, Number(event.target.value)))}
        />
      </label>

      <details className="notification-sound-settings-advanced">
        <summary>Advanced</summary>
        <ToggleRow
          id={`${baseId}-voice`}
          label="Voice notifications"
          description="When off, voice events fall back to a beep if the event still wants sound."
          checked={settings.voiceEnabled}
          onToggle={(next) => persist(setVoiceEnabled(settings, next))}
        />

        <fieldset className="notification-sound-settings-gated" disabled={!settings.masterEnabled}>
          <legend>Severity behaviour</legend>
          <div className="notification-sound-settings-grid">
            {SEVERITY_KEYS.map((key) => (
              <label key={key}>
                <span>{NOTIFICATION_SEVERITY_LABELS[key]}</span>
                <select
                  aria-label={`${NOTIFICATION_SEVERITY_LABELS[key]} severity behaviour`}
                  value={settings.severity[key]}
                  onChange={(event) => persist(setSeverityBehavior(settings, key, event.target.value as SeverityBehavior))}
                >
                  <option value="off">Off</option>
                  <option value="beep">Beep</option>
                  <option value="voice">Voice</option>
                  <option value="beep_and_voice">Beep + voice</option>
                </select>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="notification-sound-settings-gated" disabled={!settings.masterEnabled}>
          <legend>Per-event sound</legend>
          <div className="notification-sound-settings-grid">
            {EVENT_KEYS.map((key) => (
              <label key={key}>
                <span>{NOTIFICATION_EVENT_LABELS[key]}</span>
                <select
                  aria-label={`${NOTIFICATION_EVENT_LABELS[key]} sound`}
                  value={settings.events[key]}
                  onChange={(event) => persist(setEventSoundMode(settings, key, event.target.value as EventSoundMode))}
                >
                  <option value="off">Off</option>
                  <option value="beep">Beep</option>
                  <option value="voice">Voice</option>
                </select>
              </label>
            ))}
          </div>
          <small className="muted" data-testid="owner-needed-delivery">{OWNER_NEEDED_DELIVERY_NOTE}</small>
        </fieldset>

        <div className="notification-sound-settings-actions">
          <button
            type="button"
            data-testid="preview-beep"
            onClick={() => void runPreview(['sfx-routine-chime'], 'Routine chime')}
          >
            Test beep
          </button>
          <button
            type="button"
            data-testid="preview-attention"
            onClick={() => void runPreview(['sfx-attention'], 'Attention')}
          >
            Test attention
          </button>
          <button
            type="button"
            data-testid="preview-critical"
            onClick={() => void runPreview(['sfx-critical'], 'Critical')}
          >
            Test critical
          </button>
          <button
            type="button"
            data-testid="preview-voice-concat"
            onClick={() => void runPreview(composeWorkerPhraseTokens(1, 'phrase-finished'), 'Worker One finished')}
          >
            Test voice (Worker One finished)
          </button>
          <button
            type="button"
            data-testid="preview-voice-permission"
            onClick={() => void runPreview(composeWorkerPhraseTokens(3, 'phrase-needs-your-permission'), 'Worker Three permission')}
          >
            Test voice (permission)
          </button>
          <button
            type="button"
            data-testid="reset-notification-sound"
            onClick={() => {
              const defaults = resetNotificationSoundSettings();
              persist(defaults);
              setStatusLine('Restored defaults.');
            }}
          >
            Reset defaults
          </button>
        </div>
      </details>

      <p
        className="notification-sound-settings-status"
        role="status"
        data-muted={muted ? 'true' : 'false'}
        data-testid="notification-sound-status"
      >
        {muted ? 'Muted — visual notifications still show.' : statusLine}
        {settings === DEFAULT_NOTIFICATION_SOUND_SETTINGS ? '' : ''}
      </p>
    </section>
  );
}
